import { api } from '@/helpers/apiClient'
import logger, { toErrorMessage } from '@/helpers/logger'
import {
  loginResponseSchema,
  userSchema,
  type LoginInput,
  type LoginResponse,
  type User,
} from '@/schemas/auth.schema'
import type { ChangePasswordInput } from '@lib/input/auth.input'
import type { IAuthService } from '@/types/AuthService.types'

/**
 * Service for authentication endpoints. Every method validates its response
 * through the `api` client's schema boundary, so the returned values are typed
 * by the schemas, not by unchecked assertions.
 */
class AuthService implements IAuthService {
  /**
   * Log in with email and password.
   * @param input Login credentials.
   * @returns The access token and the signed-in user.
   */
  async login(input: LoginInput): Promise<LoginResponse> {
    try {
      return await api.post('/auth/login', input, loginResponseSchema)
    } catch (error) {
      logger.error('Login failed', { error: toErrorMessage(error) })
      throw error
    }
  }

  /**
   * Fetch the currently authenticated user.
   * @returns The signed-in user.
   */
  async me(): Promise<User> {
    try {
      return await api.get('/auth/me', userSchema)
    } catch (error) {
      logger.error('Failed to load current user', { error: toErrorMessage(error) })
      throw error
    }
  }

  /**
   * Change the signed-in user's password.
   *
   * Nothing is stored client-side afterwards: the existing token stays valid,
   * because it is a stateless JWT that the server has no record of and cannot
   * revoke. The user stays signed in on this device and on any other.
   */
  async changePassword(input: ChangePasswordInput): Promise<User> {
    try {
      return await api.post('/auth/password', input, userSchema)
    } catch (error) {
      // Deliberately no `input` in the log line — it holds two passwords.
      logger.error('Password change failed', { error: toErrorMessage(error) })
      throw error
    }
  }
}

export const authService = new AuthService()
